import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BarChart3, Eye, Users, Mail, TrendingUp, Loader2, Download, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin analytics panel.
 *  - Visits, unique sessions, signups (window-bounded).
 *  - Top pages, top referrers, daily trends (lightweight inline bars).
 *  - Subscribers list with CSV export + delete.
 *
 * Props:
 *   token: admin token, used for X-Admin-Token header.
 */
export default function AdminAnalytics({ token }) {
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`/admin/analytics/summary?days=${windowDays}`, { headers }),
        api.get(`/admin/subscribers?limit=500`, { headers }),
      ]);
      setSummary(s.data);
      setSubs(l.data?.subscribers || []);
    } catch (err) {
      toast.error("Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [windowDays, token]);

  useEffect(() => {
    if (token) load();
  }, [load, token]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/subscribers?format=csv&limit=5000`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "paws-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed.");
    }
  };

  const removeSub = async (id, email) => {
    if (!window.confirm(`Remove ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/subscribers/${id}`, { headers });
      setSubs((rows) => rows.filter((r) => r.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (loading && !summary) {
    return (
      <div className="border border-zinc-900 bg-white p-6 flex items-center gap-2 text-sm" data-testid="admin-analytics-loading">
        <Loader2 size={14} className="animate-spin" /> Loading analytics…
      </div>
    );
  }
  if (!summary) return null;

  const t = summary.totals;
  const maxView = Math.max(1, ...summary.daily_views.map((d) => d.views));
  const maxSub = Math.max(1, ...summary.daily_subscribers.map((d) => d.count));

  return (
    <div className="border border-zinc-900 bg-white" data-testid="admin-analytics">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-red-600" />
          <span className="label-eyebrow">Analytics · Last {summary.window_days} days</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="border border-zinc-900 bg-white text-xs font-bold uppercase tracking-widest px-2 py-1 outline-none"
            data-testid="analytics-window-select"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
            <option value={365}>1y</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="border border-zinc-900 px-3 py-1 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white"
            data-testid="analytics-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
        <Kpi icon={Eye} label="Page views" value={t.page_views_window} sub={`${t.page_views_all_time} all-time`} testid="kpi-views" />
        <Kpi icon={Users} label="Unique visitors" value={t.unique_sessions_window} sub="distinct sessions" testid="kpi-uniques" />
        <Kpi icon={Mail} label="New signups" value={t.subscribers_window} sub={`${t.subscribers_total} total`} testid="kpi-signups" />
        <Kpi icon={TrendingUp} label="Conversion" value={`${t.conversion_rate_pct}%`} sub="signups / visitors" testid="kpi-conversion" />
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200">
        <TrendBlock
          title="Page views by day"
          data={summary.daily_views}
          maxVal={maxView}
          valueKey="views"
          accent="bg-zinc-900"
          testid="trend-views"
        />
        <TrendBlock
          title="Signups by day"
          data={summary.daily_subscribers}
          maxVal={maxSub}
          valueKey="count"
          accent="bg-red-600"
          testid="trend-subs"
        />
      </div>

      {/* Top pages & referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200">
        <ListBlock
          title="Top pages"
          icon={Globe}
          rows={summary.top_pages.map((p) => ({ label: p.path, value: p.views }))}
          testid="top-pages"
        />
        <ListBlock
          title="Top referrers"
          icon={Globe}
          rows={summary.top_referrers.map((p) => ({ label: p.referrer, value: p.views }))}
          emptyText="No external referrers in this window."
          testid="top-referrers"
        />
      </div>

      {/* Subscribers */}
      <div className="bg-white px-5 py-4 border-t border-zinc-900">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-red-600" />
            <span className="label-eyebrow">Subscribers ({subs.length})</span>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white"
            data-testid="export-subs-csv"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
        {subs.length === 0 ? (
          <div className="text-sm text-zinc-500 mt-3" data-testid="subs-empty">
            No subscribers yet. Share the printable widget to grow this list.
          </div>
        ) : (
          <div className="mt-3 max-h-96 overflow-y-auto border border-zinc-200" data-testid="subs-list">
            <table className="w-full text-xs">
              <thead className="bg-zinc-100 text-left">
                <tr>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider">Email</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider">Source</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider">Signed up</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`sub-row-${s.id}`}>
                    <td className="px-3 py-2 font-mono">{s.email}</td>
                    <td className="px-3 py-2 text-zinc-600">{s.source}</td>
                    <td className="px-3 py-2 text-zinc-600">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeSub(s.id, s.email)}
                        className="text-zinc-400 hover:text-red-600"
                        title="Remove subscriber"
                        data-testid={`sub-delete-${s.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon size={12} />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-display font-black text-3xl mt-1 tracking-tighter">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

function TrendBlock({ title, data, maxVal, valueKey, accent, testid }) {
  return (
    <div className="bg-white p-4" data-testid={testid}>
      <div className="label-eyebrow">{title}</div>
      <div className="flex items-end gap-1 mt-3 h-24">
        {data.length === 0 ? (
          <div className="text-xs text-zinc-400">No data in window.</div>
        ) : (
          data.map((d) => {
            const h = Math.max(2, Math.round((d[valueKey] / maxVal) * 90));
            return (
              <div
                key={d.day}
                className={`${accent} flex-1 min-w-[3px]`}
                style={{ height: `${h}px` }}
                title={`${d.day}: ${d[valueKey]}`}
              />
            );
          })
        )}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 font-mono mt-2">
        <span>{data[0]?.day || ""}</span>
        <span>{data[data.length - 1]?.day || ""}</span>
      </div>
    </div>
  );
}

function ListBlock({ title, icon: Icon, rows, emptyText, testid }) {
  return (
    <div className="bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon size={12} />
        <span className="label-eyebrow">{title}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-400 mt-3">{emptyText || "No data."}</div>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between gap-3 text-xs">
              <span className="font-mono truncate" title={r.label}>{r.label}</span>
              <span className="font-bold tabular-nums flex-shrink-0">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
